import React from "react";
import style from "../../calculator.scss";
import otherIncomeStyle from "./income-section.scss";
import { UseFormTools } from "@wisr/common";
import { Serviceability } from "../../calculator";
import { FREQUENCY_OPTIONS, MaritalStatus } from "../../calculator.consts";
import { Field, Icon } from "@wisr/react-ui";
import { FormValuesLabels } from "../../../../types/common-values";

export const isPartnered = (maritalStatus: string) =>
  [MaritalStatus.MARRIED, MaritalStatus.DE_FACTO].includes(
    maritalStatus as MaritalStatus
  );

export const MAX_OTHER_INCOME = 3;

export const removeOtherIncome = (
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  otherIncomeIndex: number,
  otherIncomeLength: number,
  setOtherIncomeLength: (v: number) => void
) => {
  if (otherIncomeIndex !== otherIncomeLength) {
    for (let i = otherIncomeIndex; i < otherIncomeLength; i++) {
      setValue(
        `otherIncomeSource_${i}`,
        formData[`otherIncomeSource_${i + 1}`]
      );
      setValue(
        `otherIncomeAmount_${i}`,
        formData[`otherIncomeAmount_${i + 1}`]
      );
      setValue(
        `otherIncomeFrequency_${i}`,
        formData[`otherIncomeFrequency_${i + 1}`]
      );
    }
  }

  setValue(`otherIncomeSource_${otherIncomeLength}`, "");
  setValue(`otherIncomeAmount_${otherIncomeLength}`, "0");
  setValue(`otherIncomeFrequency_${otherIncomeLength}`, "");
  setOtherIncomeLength(otherIncomeLength - 1);
};

export const existingOtherIncomeCount = (
  formData: UseFormTools<Serviceability>["formData"]
) => {
  for (let i = 1; i <= MAX_OTHER_INCOME; i++) {
    if (
      formData[`otherIncomeSource_${i}`] === undefined ||
      (formData[`otherIncomeSource_${i}`] === "" &&
        +formData[`otherIncomeAmount_${i}`] === 0)
    ) {
      return i - 1;
    }
  }

  return MAX_OTHER_INCOME;
};

export const getOtherIncomeFieldSet = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  otherIncomeIndex: number,
  setOtherIncomeLength: (v: number) => void,
  otherIncomeLength: number,
  incomeSourceOptions: FormValuesLabels["incomeSources"]
) => {
  const otherIncomeFieldHidden = otherIncomeIndex > otherIncomeLength;

  return (
    <div
      key={otherIncomeIndex}
      className={otherIncomeFieldHidden ? "hidden" : "other-income-field-set"}
    >
      <style jsx>{style}</style>
      <style jsx>{otherIncomeStyle}</style>
      <div className="other-income-header">
        <span>Other Income {otherIncomeIndex}</span>
        <div className="delete-icon">
          <a
            onClick={() =>
              removeOtherIncome(
                formData,
                setValue,
                otherIncomeIndex,
                otherIncomeLength,
                setOtherIncomeLength
              )
            }
          >
            <Icon name="action-trash-outline" size="sm" />
          </a>
        </div>
      </div>
      <div className="input-row">
        <Field
          type="select"
          label="Income source"
          options={incomeSourceOptions}
          placeholder="Select..."
          {...fieldAdapter({
            name: `otherIncomeSource_${otherIncomeIndex}`,
            condition: !otherIncomeFieldHidden,
          })}
        />
        <Field
          type="currency"
          label="Income (before tax)"
          {...fieldAdapter({
            name: `otherIncomeAmount_${otherIncomeIndex}`,
            condition: !otherIncomeFieldHidden,
          })}
        />
        <Field
          label="Frequency"
          type="select"
          options={FREQUENCY_OPTIONS}
          placeholder="Select..."
          {...fieldAdapter({
            name: `otherIncomeFrequency_${otherIncomeIndex}`,
            condition: !otherIncomeFieldHidden,
          })}
        />
      </div>
    </div>
  );
};

export const getOtherIncomeList = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  otherIncomeLength: number,
  setOtherIncomeLength: (v: number) => void,
  maxOtherIncomeLength: number,
  incomeSourceOptions: FormValuesLabels["incomeSources"]
) => {
  const otherIncomeFieldSets: JSX.Element[] = [];
  if (maxOtherIncomeLength > 0) {
    for (let i = 1; i <= maxOtherIncomeLength; i++) {
      otherIncomeFieldSets.push(
        getOtherIncomeFieldSet(
          fieldAdapter,
          formData,
          setValue,
          i,
          setOtherIncomeLength,
          otherIncomeLength,
          incomeSourceOptions
        )
      );
    }
    return otherIncomeFieldSets;
  }
  return null;
};

export const existingSecondApplicantOtherIncomeCount = (
  formData: UseFormTools<Serviceability>["formData"]
) => {
  for (let i = 1; i <= MAX_OTHER_INCOME; i++) {
    if (
      formData[`secondApplicantOtherIncomeSource_${i}`] === undefined ||
      (formData[`secondApplicantOtherIncomeSource_${i}`] === "" &&
        +formData[`secondApplicantOtherIncomeAmount_${i}`] === 0)
    ) {
      return i - 1;
    }
  }

  return MAX_OTHER_INCOME;
};

export const removeSecondApplicantOtherIncome = (
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  otherIncomeIndex: number,
  otherIncomeLength: number,
  setOtherIncomeLength: (v: number) => void
) => {
  if (otherIncomeIndex !== otherIncomeLength) {
    for (let i = otherIncomeIndex; i < otherIncomeLength; i++) {
      setValue(
        `secondApplicantOtherIncomeSource_${i}`,
        formData[`secondApplicantOtherIncomeSource_${i + 1}`]
      );
      setValue(
        `secondApplicantOtherIncomeAmount_${i}`,
        formData[`secondApplicantOtherIncomeAmount_${i + 1}`]
      );
      setValue(
        `secondApplicantOtherIncomeFrequency_${i}`,
        formData[`secondApplicantOtherIncomeFrequency_${i + 1}`]
      );
    }
  }

  setValue(`secondApplicantOtherIncomeSource_${otherIncomeLength}`, "");
  setValue(`secondApplicantOtherIncomeAmount_${otherIncomeLength}`, "0");
  setValue(`secondApplicantOtherIncomeFrequency_${otherIncomeLength}`, "");
  setOtherIncomeLength(otherIncomeLength - 1);
};

export const getSecondApplicantOtherIncomeFieldSet = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  otherIncomeIndex: number,
  setOtherIncomeLength: (v: number) => void,
  otherIncomeLength: number,
  incomeSourceOptions: FormValuesLabels["incomeSources"]
) => {
  const otherIncomeFieldHidden = otherIncomeIndex > otherIncomeLength;

  return (
    <div
      key={otherIncomeIndex}
      className={otherIncomeFieldHidden ? "hidden" : "other-income-field-set"}
    >
      <style jsx>{style}</style>
      <style jsx>{otherIncomeStyle}</style>
      <div className="other-income-header">
        <span>Other Income {otherIncomeIndex}</span>
        <div className="delete-icon">
          <a
            onClick={() =>
              removeSecondApplicantOtherIncome(
                formData,
                setValue,
                otherIncomeIndex,
                otherIncomeLength,
                setOtherIncomeLength
              )
            }
          >
            <Icon name="action-trash-outline" size="sm" />
          </a>
        </div>
      </div>
      <div className="input-row">
        <Field
          type="select"
          label="Income source"
          options={incomeSourceOptions}
          placeholder="Select..."
          {...fieldAdapter({
            name: `secondApplicantOtherIncomeSource_${otherIncomeIndex}`,
            condition: !otherIncomeFieldHidden,
          })}
        />
        <Field
          type="currency"
          label="Income (before tax)"
          {...fieldAdapter({
            name: `secondApplicantOtherIncomeAmount_${otherIncomeIndex}`,
            condition: !otherIncomeFieldHidden,
          })}
        />
        <Field
          label="Frequency"
          type="select"
          options={FREQUENCY_OPTIONS}
          placeholder="Select..."
          {...fieldAdapter({
            name: `secondApplicantOtherIncomeFrequency_${otherIncomeIndex}`,
            condition: !otherIncomeFieldHidden,
          })}
        />
      </div>
    </div>
  );
};

export const getSecondApplicantOtherIncomeList = (
  fieldAdapter: UseFormTools<Serviceability>["fieldAdapter"],
  formData: UseFormTools<Serviceability>["formData"],
  setValue: UseFormTools<Serviceability>["setValue"],
  otherIncomeLength: number,
  setOtherIncomeLength: (v: number) => void,
  maxOtherIncomeLength: number,
  incomeSourceOptions: FormValuesLabels["incomeSources"]
) => {
  const otherIncomeFieldSets: JSX.Element[] = [];
  if (maxOtherIncomeLength > 0) {
    for (let i = 1; i <= maxOtherIncomeLength; i++) {
      otherIncomeFieldSets.push(
        getSecondApplicantOtherIncomeFieldSet(
          fieldAdapter,
          formData,
          setValue,
          i,
          setOtherIncomeLength,
          otherIncomeLength,
          incomeSourceOptions
        )
      );
    }
    return otherIncomeFieldSets;
  }
  return null;
};
